// Sportmonks v3 player-statistic details; season/team scope is validated before use.
export const POSITION_OPTIONS = [
  "TW",
  "RV",
  "LV",
  "IV",
  "DM",
  "ZM",
  "ZOM",
  "RM",
  "LM",
  "RF",
  "LF",
  "HS",
  "ST",
];
const definitions = [
  ["TW", 0, ["goalkeeper"]],
  ["RV", 1, ["right-back", "right-wing-back"]],
  ["LV", 1, ["left-back", "left-wing-back"]],
  ["IV", 1, ["centre-back", "center-back"]],
  ["DM", 2, ["defensive-midfield", "defensive-midfied"]],
  ["ZM", 2, ["central-midfield", "central-midfied"]],
  ["ZOM", 2, ["attacking-midfield", "attacking-midfied"]],
  ["RM", 2, ["right-midfield"]],
  ["LM", 2, ["left-midfield"]],
  ["RF", 3, ["right-winger", "right-wing"]],
  ["LF", 3, ["left-winger", "left-wing"]],
  ["HS", 3, ["secondary-striker", "second-striker"]],
  ["ST", 3, ["centre-forward", "center-forward", "striker"]],
];
const clean = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replaceAll("_", "-")
    .replaceAll(" ", "-");
export function playerPosition(player, override) {
  const detailed = player.detailedPosition;
  const match = definitions.find(([label, , names]) =>
    override
      ? label === override
      : names.includes(clean(detailed?.developer_name)) ||
        names.includes(clean(detailed?.code)) ||
        names.includes(clean(detailed?.name))
  );
  if (match)
    return {
      label: match[0],
      group: match[1],
      order: POSITION_OPTIONS.indexOf(match[0]),
      manual: !!override,
      fallback: false,
    };
  const group = {
    goalkeeper: 0,
    gk: 0,
    defender: 1,
    def: 1,
    midfielder: 2,
    mid: 2,
    attacker: 3,
    forward: 3,
    att: 3,
  }[clean(player.position)];
  return {
    label:
      group === 0
        ? "TW"
        : group === 1
        ? "DEF"
        : group === 2
        ? "MID"
        : group === 3
        ? "ATT"
        : "–",
    group: group ?? 4,
    order: 99,
    fallback: group !== 0,
    manual: false,
  };
}
export function playerAge(dateOfBirth, now = new Date()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth || "")) return null;
  const [y, m, d] = dateOfBirth.split("-").map(Number),
    birth = new Date(y, m - 1, d);
  if (
    birth.getFullYear() !== y ||
    birth.getMonth() !== m - 1 ||
    birth.getDate() !== d
  )
    return null;
  const age =
    now.getFullYear() -
    y -
    (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)
      ? 1
      : 0);
  return age >= 0 ? age : null;
}
const numeric = (v) =>
  v !== null &&
  v !== undefined &&
  v !== "" &&
  typeof v !== "boolean" &&
  Number.isFinite(Number(v)) &&
  Number(v) >= 0
    ? Number(v)
    : null;
export function squadStatistics(entry, scope) {
  const empty = {
    appearances: null,
    minutes: null,
    averageMinutes: null,
    goals: null,
    assists: null,
    rating: null,
    yellow: null,
    red: null,
    yellowRed: null,
    captainGames: null,
  };
  if (
    !scope ||
    Number(entry.season_id) !== Number(scope.seasonId) ||
    Number(entry.team_id) !== Number(scope.teamId) ||
    !scope.leagueId ||
    Number(entry.season?.league_id) !== Number(scope.leagueId)
  )
    return empty;
  // A missing detail is not evidence of zero. Do not aggregate other seasons or teams.
  const detail = (id) => {
    const rows = (Array.isArray(entry.details) ? entry.details : []).filter(
      (d) => Number(d.type_id) === id
    );
    return rows.length === 1 ? rows[0].value : null;
  };
  const total = (id) => numeric(detail(id)?.total);
  const appearances = total(321),
    minutes = total(119),
    rating = numeric(detail(118)?.average);
  return {
    appearances,
    minutes,
    averageMinutes:
      appearances > 0 && minutes !== null ? minutes / appearances : null,
    goals: total(52),
    assists: total(79),
    rating: rating > 0 && rating <= 10 ? rating : null,
    yellow: total(84),
    red: total(83),
    yellowRed: total(85),
    captainGames: total(40),
  };
}
export function normalizeSquadPlayer(entry, scope) {
  const p = entry?.player || {},
    position = entry?.position || p.position || {},
    nationality = p.nationality || entry.nationality || {};
  const fullName = (
    p.display_name ||
    p.name ||
    entry.player_name ||
    p.common_name ||
    "–"
  ).trim();
  const name =
    fullName.split(/\s+/).length > 3 && p.common_name
      ? p.common_name.trim()
      : fullName;
  return {
    id: Number(p.id ?? entry.player_id ?? entry.id),
    teamId: Number(entry.team_id),
    name,
    fullName,
    image: p.image_path || entry.image_path || null,
    position: position.name || position.code || entry.position_name || "–",
    detailedPosition:
      entry.detailedposition ||
      entry.detailedPosition ||
      p.detailedposition ||
      p.detailedPosition ||
      null,
    number: entry.jersey_number ?? p.jersey_number ?? null,
    dateOfBirth: p.date_of_birth || null,
    age: playerAge(p.date_of_birth),
    nationality: nationality.name || p.nationality_name || null,
    flag: nationality.image_path || null,
    height: p.height ?? null,
    weight: p.weight ?? null,
    statistics: squadStatistics(entry, scope),
  };
}
export function sortSquad(players, overrides = {}) {
  return [...players].sort((a, b) => {
    const ap = playerPosition(a, overrides[a.id]),
      bp = playerPosition(b, overrides[b.id]);
    return (
      ap.group - bp.group ||
      ap.order - bp.order ||
      (b.statistics?.averageMinutes ?? -1) -
        (a.statistics?.averageMinutes ?? -1)
    );
  });
}
export const squadNumber = (value, digits = 0) =>
  numeric(value) === null
    ? "–"
    : Number(value).toLocaleString("de-DE", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
